import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { setTitle, setMeta, setCanonical, setJsonLd } from "@/lib/seo";
import { Loader2, ArrowLeft } from "lucide-react";

function renderBody(body) {
  const raw = body || "";
  const looksHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
  const html = looksHtml ? raw : marked.parse(raw, { breaks: true });
  return DOMPurify.sanitize(html);
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    api.get(`/blog/posts/${slug}`)
      .then(({ data }) => {
        if (!active) return;
        setPost(data);
        setState("ok");
        const url = `${window.location.origin}/blog/${data.slug}`;
        const title = `${data.title} | Saving America Money`;
        const desc = data.meta_description || data.excerpt || "";
        setTitle(title);
        setMeta("description", desc);
        setMeta("og:title", data.title, "property");
        setMeta("og:description", desc, "property");
        setMeta("og:type", "article", "property");
        setMeta("og:url", url, "property");
        setMeta("twitter:card", "summary_large_image");
        setCanonical(url);
        setJsonLd("blog-post-jsonld", {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: data.title,
          description: desc,
          datePublished: data.created_at,
          dateModified: data.updated_at || data.created_at,
          mainEntityOfPage: url,
          publisher: { "@type": "Organization", name: "Saving America Money" },
        });
      })
      .catch(() => active && setState("missing"));
    return () => { active = false; };
  }, [slug]);

  if (state === "loading") {
    return <div className="py-32 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;
  }
  if (state === "missing") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-28 text-center" data-testid="blog-post-missing">
        <h1 className="font-display font-bold text-sam-navy text-2xl">Article not found</h1>
        <button onClick={() => navigate("/blog")} className="mt-5 inline-flex items-center gap-2 text-sam-crimson font-semibold">
          <ArrowLeft className="h-4 w-4" /> Back to the blog
        </button>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-3xl px-6 py-14" data-testid="blog-post">
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sam-slate hover:text-sam-crimson" data-testid="blog-back">
        <ArrowLeft className="h-4 w-4" /> All articles
      </Link>
      <div className="mt-6 text-[11px] font-semibold uppercase tracking-wider text-sam-crimson">
        {(post.created_at || "").slice(0, 10)}
      </div>
      <h1 className="mt-2 font-display font-extrabold text-sam-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.08]" data-testid="blog-post-title">
        {post.title}
      </h1>
      {post.meta_description && <p className="mt-4 text-lg text-sam-slate leading-relaxed">{post.meta_description}</p>}
      <div className="sam-flag-bar h-1 w-24 my-8 rounded-full" />
      <div
        className="sam-prose max-w-none"
        data-testid="blog-post-body"
        dangerouslySetInnerHTML={{ __html: renderBody(post.body) }}
      />
    </article>
  );
}
